import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll for navbar
  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', () => {
      setIsScrolled(window.scrollY > 50);
    });
  }

  const faqs = [
    {
      question: "How does the VPN protect my data?",
      answer: "Our VPN uses military-grade 256-bit encryption to secure all your internet traffic. This means your data is encrypted before it leaves your device, making it unreadable to hackers, ISPs, and other third parties. We create a secure tunnel between your device and our servers, ensuring complete privacy."
    },
    {
      question: "Which devices are supported?",
      answer: "SARKIS VPN supports all major platforms including Windows, macOS, Linux, iOS, and Android. You can protect your smartphones, tablets, laptops, and desktop computers with a single subscription."
    },
    {
      question: "Can I use it while traveling?",
      answer: "Absolutely! Our VPN is perfect for travelers. With servers in multiple countries, you can access your favorite content from anywhere in the world while keeping your connection secure on public Wi-Fi networks."
    },
    {
      question: "What is your refund policy?",
      answer: "We offer a 30-day money-back guarantee. If you're not completely satisfied with our service, contact us within 30 days of purchase for a full refund, no questions asked."
    },
    {
      question: "How many devices can I connect?",
      answer: "You can connect up to 5 devices simultaneously with a single SARKIS VPN account. This allows you to protect all your devices - phones, tablets, and computers - at the same time."
    },
    {
      question: "Do you keep any logs?",
      answer: "No. We have a strict zero-log policy. We don't track, collect, or store any of your online activity. Your privacy is our top priority, and we've designed our infrastructure to ensure we cannot access your browsing data even if we wanted to."
    }
  ];

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="text-2xl font-bold tracking-wide">
            <span className={`${isScrolled ? 'text-gray-900' : 'text-white'}`}>SARKIS</span>
            <span className="text-cyan-400"> S.R.L.</span>
          </div>
          <div className="flex items-center gap-8">
            <button onClick={() => scrollToSection('home')} className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-cyan-500' : 'text-white hover:text-cyan-300'}`}>Home</button>
            <button onClick={() => scrollToSection('about')} className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-cyan-500' : 'text-white hover:text-cyan-300'}`}>About</button>
            <button onClick={() => scrollToSection('services')} className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-cyan-500' : 'text-white hover:text-cyan-300'}`}>Services</button>
            <button onClick={() => scrollToSection('contact')} className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-cyan-500' : 'text-white hover:text-cyan-300'}`}>Contact</button>
            <button onClick={() => scrollToSection('faq')} className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-cyan-500' : 'text-white hover:text-cyan-300'}`}>FAQ</button>
            <button onClick={() => scrollToSection('terms')} className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-cyan-500' : 'text-white hover:text-cyan-300'}`}>Terms</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/741d5d64f4e86092cc2692019b4d2722.jpg" 
            alt="Secure Digital Network"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="relative z-10 text-center px-6 w-full max-w-5xl">
          <h1 className="mb-6">
            <div className="text-7xl font-extrabold text-white tracking-tight mb-2">SECURE YOUR</div>
            <div className="text-6xl italic font-serif bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent mb-2">Digital Freedom</div>
            <div className="text-7xl font-extrabold text-white tracking-tight">ANYWHERE</div>
          </h1>
          <p className="text-lg text-white/70 mb-10 font-light">Enterprise-grade VPN protection for your privacy</p>
          <button onClick={() => scrollToSection('contact')} className="bg-cyan-400 hover:bg-cyan-500 text-white px-12 py-5 rounded-full text-base font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer">
            Get Protected Now
            <i className="ri-arrow-right-line text-xl"></i>
          </button>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-4">WHO WE ARE</p>
            <h2 className="text-5xl font-bold text-gray-900 leading-tight">
              Protecting Your<br />
              <span className="italic font-serif text-blue-600">Privacy</span><br />
              Since Day One
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            <div className="bg-gradient-to-br from-orange-400 to-red-400 rounded-3xl p-10 shadow-xl transform hover:-rotate-1 transition-transform duration-300 relative">
              <div className="absolute top-6 right-6 w-10 h-10 bg-white rounded-full flex items-center justify-center cursor-pointer hover:scale-110 transition-transform">
                <i className="ri-arrow-right-up-line text-orange-500 text-xl"></i>
              </div>
              <div className="text-white">
                <div className="text-6xl font-serif mb-3">100%</div>
                <div className="text-2xl font-semibold mb-2">Network Uptime</div>
                <div className="text-base italic opacity-90">Always Connected</div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-300 to-indigo-300 rounded-3xl p-10 shadow-xl transform hover:rotate-1 transition-transform duration-300 relative">
              <div className="absolute top-6 right-6 w-10 h-10 bg-white rounded-full flex items-center justify-center cursor-pointer hover:scale-110 transition-transform">
                <i className="ri-arrow-right-up-line text-purple-600 text-xl"></i>
              </div>
              <div className="text-white">
                <div className="text-6xl font-serif mb-3">256</div>
                <div className="text-xl font-semibold mb-2">Bit Encryption<br />Standard</div>
                <div className="text-base italic opacity-90">Military-Grade Security</div>
              </div>
            </div>
          </div>

          <div className="mt-16 max-w-3xl mx-auto text-center">
            <p className="text-base text-gray-600 leading-relaxed mb-4">
              SARKIS S.R.L. is a leading technology company based in Trelew, Argentina, dedicated to developing cutting-edge applications that prioritize user privacy and security. Our flagship VPN solution represents years of expertise in cybersecurity and network protection.
            </p>
            <p className="text-base text-gray-600 leading-relaxed">
              We believe that everyone deserves the right to browse the internet freely and securely. Our mission is to provide enterprise-level protection that's accessible to individuals and businesses alike, ensuring your digital freedom is never compromised.
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">OUR TECHNOLOGY</p>
            <h2 className="text-5xl font-bold text-gray-900">What Makes Us Different</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-rose-50 rounded-3xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300 h-[480px] flex flex-col">
              <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-32 h-32 flex items-center justify-center">
                  <i className="ri-shield-check-line text-8xl text-rose-400"></i>
                </div>
              </div>
              <div className="bg-white p-6 text-center">
                <h3 className="text-base font-semibold text-gray-900">Zero-Log Policy</h3>
              </div>
            </div>

            <div className="bg-emerald-50 rounded-3xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300 h-[480px] flex flex-col">
              <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-32 h-32 flex items-center justify-center">
                  <i className="ri-global-line text-8xl text-emerald-400"></i>
                </div>
              </div>
              <div className="bg-white p-6 text-center">
                <h3 className="text-base font-semibold text-gray-900">Global Servers</h3>
              </div>
            </div>

            <div className="bg-gray-100 rounded-3xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300 h-[480px] flex flex-col">
              <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-32 h-32 flex items-center justify-center">
                  <i className="ri-lock-line text-8xl text-gray-400"></i>
                </div>
              </div>
              <div className="bg-white p-6 text-center">
                <h3 className="text-base font-semibold text-gray-900">Kill Switch Protection</h3>
              </div>
            </div>

            <div className="bg-amber-50 rounded-3xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300 h-[480px] flex flex-col">
              <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-32 h-32 flex items-center justify-center">
                  <i className="ri-speed-line text-8xl text-amber-400"></i>
                </div>
              </div>
              <div className="bg-white p-6 text-center">
                <h3 className="text-base font-semibold text-gray-900">Lightning Speed</h3>
              </div>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-eye-off-line text-3xl text-cyan-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Complete Privacy</h3>
              <p className="text-sm text-gray-600">Your online activities remain completely private with our strict no-logs policy and advanced encryption.</p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-earth-line text-3xl text-cyan-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Worldwide Access</h3>
              <p className="text-sm text-gray-600">Connect to servers around the globe and access content from anywhere without restrictions.</p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-customer-service-line text-3xl text-cyan-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">24/7 Support</h3>
              <p className="text-sm text-gray-600">Our dedicated support team is always available to help you with any questions or concerns.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="grid grid-cols-1 lg:grid-cols-2 min-h-[600px]">
        <div className="relative bg-gradient-to-br from-teal-600 to-blue-700 p-16 flex items-end overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 w-32 h-32 border-2 border-white rounded-full"></div>
            <div className="absolute top-40 right-20 w-24 h-24 border-2 border-white rounded-full"></div>
            <div className="absolute bottom-20 left-1/3 w-40 h-40 border-2 border-white rounded-full"></div>
          </div>
          <div className="relative z-10">
            <p className="text-xs text-white/80 uppercase tracking-widest mb-4">GET IN TOUCH</p>
            <h2 className="text-white leading-tight">
              <div className="text-6xl font-bold mb-1">READY TO</div>
              <div className="text-7xl font-extrabold mb-1">SECURE</div>
              <div className="text-6xl font-bold">YOUR DATA?</div>
            </h2>
          </div>
        </div>

        <div className="bg-white p-16 flex items-center justify-center">
          <div className="w-full max-w-md">
            <h3 className="text-4xl font-bold text-gray-900 text-center mb-10">Contact<br />SARKIS S.R.L.</h3>
            
            <div className="space-y-3 text-center mb-12">
              <p className="text-base text-gray-600">
                <a href="mailto:information@sarkissrl.com" className="hover:text-cyan-600 transition-colors hover:underline">information@sarkissrl.com</a>
              </p>
              <p className="text-base text-gray-600">
                <a href="tel:+16592575974" className="hover:text-cyan-600 transition-colors hover:underline">+1 6592575974</a>
              </p>
              <p className="text-base text-gray-600">BELGRANO 340</p>
              <p className="text-base text-gray-600">Trelew, U9100HVF</p>
              <p className="text-base text-gray-600">Argentina</p>
            </div>

            <div className="text-center">
              <a href="mailto:information@sarkissrl.com" className="inline-flex items-center gap-3 bg-gray-800 hover:bg-gray-900 text-white px-11 py-4 rounded-full text-base font-semibold transition-all duration-300 shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer">
                Send Message
                <i className="ri-arrow-right-line text-lg"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block bg-gray-200 text-gray-700 text-xs uppercase tracking-wide px-4 py-2 rounded-full mb-4">
              FREQUENTLY ASKED
            </div>
            <h2 className="text-5xl font-bold text-gray-900">Common Questions</h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <span className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <div className="flex-shrink-0 w-7 h-7 rounded-full border-2 border-gray-300 flex items-center justify-center">
                    <i className={`${openFaq === index ? 'ri-subtract-line' : 'ri-add-line'} text-gray-600`}></i>
                  </div>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5">
                    <p className="text-base text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Terms Section */}
      <section id="terms" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-5xl font-bold text-gray-900 mb-12 text-center">Terms of Service</h2>
          
          <div className="prose prose-lg max-w-none">
            <div className="mb-8">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                By accessing and using SARKIS S.R.L. VPN services, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">2. Use License</h3>
              <p className="text-base text-gray-600 leading-relaxed mb-3">
                Permission is granted to temporarily use SARKIS VPN for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-base text-gray-600">
                <li>Modify or copy the materials</li>
                <li>Use the materials for any commercial purpose or for any public display</li>
                <li>Attempt to reverse engineer any software contained in SARKIS VPN service</li>
                <li>Remove any copyright or other proprietary notations from the materials</li>
                <li>Transfer the materials to another person or mirror the materials on any other server</li>
              </ul>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">3. Privacy Policy</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                SARKIS S.R.L. maintains a strict no-logs policy. We do not track, collect, or store any information about your online activities. Your privacy is our utmost priority, and we have designed our infrastructure to ensure we cannot access your data.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">4. Prohibited Activities</h3>
              <p className="text-base text-gray-600 leading-relaxed mb-3">
                You agree not to use the VPN service for any unlawful purpose or any purpose prohibited under this clause. You agree not to use the service in any way that could damage the service or impair anyone else's use of it.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">5. Service Availability</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                While we strive to maintain 100% uptime, SARKIS S.R.L. does not guarantee that the service will be uninterrupted or error-free. We reserve the right to suspend or terminate the service for maintenance or other reasons.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">6. Limitation of Liability</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                In no event shall SARKIS S.R.L. or its suppliers be liable for any damages arising out of the use or inability to use the service, even if SARKIS S.R.L. has been notified of the possibility of such damages.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">7. Refund Policy</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                We offer a 30-day money-back guarantee. If you are not satisfied with our service, you may request a full refund within 30 days of your initial purchase by contacting our support team.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">8. Modifications to Terms</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                SARKIS S.R.L. reserves the right to revise these terms of service at any time without notice. By using this service, you are agreeing to be bound by the current version of these terms of service.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">9. Governing Law</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                These terms and conditions are governed by and construed in accordance with the laws of Argentina, and you irrevocably submit to the exclusive jurisdiction of the courts in that location.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">10. Contact Information</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                If you have any questions about these Terms of Service, please contact us at information@sarkissrl.com or call us at +1 6592575974.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <i className="ri-global-line text-[800px] absolute -bottom-40 -right-40"></i>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 mb-12">
            <div className="lg:col-span-2">
              <div className="text-2xl font-bold mb-6">
                SARKIS <span className="text-cyan-400">S.R.L.</span>
              </div>
              <div className="flex items-center gap-3 mb-8">
                <input 
                  type="email" 
                  placeholder="Your email address" 
                  className="flex-1 bg-gray-800 border border-gray-700 rounded-full px-5 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400"
                />
                <button className="w-12 h-12 bg-cyan-400 hover:bg-cyan-500 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-notification-line text-white text-xl"></i>
                </button>
              </div>
              <div className="text-3xl font-serif leading-tight">
                Stay Updated with<br />Security News
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4 text-gray-400">COMPANY</h4>
              <ul className="space-y-3">
                <li><button onClick={() => scrollToSection('about')} className="text-sm hover:text-cyan-400 transition-colors cursor-pointer">About Us</button></li>
                <li><button onClick={() => scrollToSection('services')} className="text-sm hover:text-cyan-400 transition-colors cursor-pointer">Our Technology</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="text-sm hover:text-cyan-400 transition-colors cursor-pointer">Contact</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4 text-gray-400">LEGAL</h4>
              <ul className="space-y-3">
                <li><button onClick={() => scrollToSection('terms')} className="text-sm hover:text-cyan-400 transition-colors cursor-pointer">Terms of Service</button></li>
                <li><button onClick={() => scrollToSection('terms')} className="text-sm hover:text-cyan-400 transition-colors cursor-pointer">Privacy Policy</button></li>
                <li><button onClick={() => scrollToSection('terms')} className="text-sm hover:text-cyan-400 transition-colors cursor-pointer">Cookie Policy</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4 text-gray-400">SUPPORT</h4>
              <ul className="space-y-3">
                <li><button onClick={() => scrollToSection('faq')} className="text-sm hover:text-cyan-400 transition-colors cursor-pointer">FAQ</button></li>
                <li><a href="mailto:information@sarkissrl.com" className="text-sm hover:text-cyan-400 transition-colors cursor-pointer">Help Center</a></li>
                <li><a href="mailto:information@sarkissrl.com" className="text-sm hover:text-cyan-400 transition-colors cursor-pointer">Documentation</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-sm text-gray-400">
              © 2025 SARKIS S.R.L. All rights reserved
            </div>

            <div className="flex items-center gap-5">
              <a href="#" className="w-9 h-9 border border-gray-700 rounded-full flex items-center justify-center hover:bg-cyan-400 hover:border-cyan-400 transition-all cursor-pointer">
                <i className="ri-linkedin-fill text-base"></i>
              </a>
              <a href="#" className="w-9 h-9 border border-gray-700 rounded-full flex items-center justify-center hover:bg-cyan-400 hover:border-cyan-400 transition-all cursor-pointer">
                <i className="ri-twitter-x-fill text-base"></i>
              </a>
              <a href="#" className="w-9 h-9 border border-gray-700 rounded-full flex items-center justify-center hover:bg-cyan-400 hover:border-cyan-400 transition-all cursor-pointer">
                <i className="ri-facebook-fill text-base"></i>
              </a>
              <a href="#" className="w-9 h-9 border border-gray-700 rounded-full flex items-center justify-center hover:bg-cyan-400 hover:border-cyan-400 transition-all cursor-pointer">
                <i className="ri-instagram-fill text-base"></i>
              </a>
            </div>

            <div className="text-sm text-gray-400">
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
